import type { ReactNode } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { LogIn } from "lucide-react";
import { loginRequest } from "@/lib/msal";

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Gates a route behind Entra sign-in. When signed out, shows a centred
 * sign-in prompt instead of the protected content.
 */
export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const signingIn = inProgress !== "none";

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-neusim-bg px-4 text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full"
        style={{ background: "#a855f7", opacity: 0.2, filter: "blur(180px)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full"
        style={{ background: "#22d3ee", opacity: 0.15, filter: "blur(180px)" }}
      />
      <div className="relative max-w-sm rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-8 text-center backdrop-blur-xl">
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
        <h1 className="bg-gradient-to-r from-white via-fuchsia-100 to-cyan-100 bg-clip-text text-2xl font-bold text-transparent">
          Welcome to NeuSim
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-white/70">
          Sign in with your organisation account to access your leadership simulations.
        </p>
        <button
          type="button"
          disabled={signingIn}
          onClick={() => void instance.loginRedirect(loginRequest)}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <LogIn className="h-4 w-4" />
          {signingIn ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </div>
  );
}
